import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { BugIcon, LogOutIcon, SettingsIcon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

import Error from "@/shared/components/Error";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { useLogout } from "@/shared/hooks/useLogout";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { User } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";

export function UserMenu() {
  const { logout, isLoggingOut } = useLogout();

  const {
    data: user,
    isPending: isUserPending,
    error: userError,
    refetch: refetchUser,
  } = useQuery<User, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
  });

  const { open: userErrorModalOpen, setOpen: setUserErrorModalOpen, handleRetry: handleUserRetry } = useErrorModal(userError, refetchUser);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="hidden rounded-full md:flex" aria-label="Open user menu" disabled={isUserPending}>
            {isUserPending || userError ? (
              <Skeleton className="size-8 rounded-full" />
            ) : (
              <Avatar size="default">
                <AvatarImage src="" alt={[user.firstName, user.lastName].join(" ")} />
                <AvatarFallback>{[user.firstName[0].toUpperCase(), user.lastName[0].toUpperCase()].join("")}</AvatarFallback>
              </Avatar>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="normal-case tracking-normal">
            {isUserPending || userError ? (
              <div className="flex flex-col gap-1.5 py-0.5">
                <Skeleton className="h-3 w-24 rounded" />
                <Skeleton className="h-3 w-32 rounded" />
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold text-foreground">{[user.firstName, user.lastName].join(" ")}</p>
                <p className="text-xs font-normal text-muted-foreground">{user.emailAddress}</p>
              </>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link to="/profile" className="cursor-pointer">
                <UserIcon />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <SettingsIcon />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/notifications" className="cursor-pointer">
                <BugIcon />
                Notifications
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" variant="destructive" onSelect={logout} disabled={isLoggingOut}>
            <LogOutIcon />
            {isLoggingOut ? "Signing out..." : "Sign out"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {userError && <Error variant="modal" title="Failed to load user" error={userError} open={userErrorModalOpen} onOpenChange={setUserErrorModalOpen} onRetry={handleUserRetry} />}
    </>
  );
}
